import { BcmPaymentType, IPerson, Person } from '@shared/models/person';
import { InvoicePosition, InvoicePositionDto } from '@shared/models/invoice-position';
import { EventEmitter } from '@angular/core';
import { Company, ICompany } from '@shared/models/company';
import { Tenant } from '@shared/models/tenant';
import { SafeUrl } from '@angular/platform-browser';
import { InvoiceTypes, ReceiptTypes } from '@shared/invoice-types';
import { isValid, parseISO } from '@core/date.facade';
import { naturalSort } from '@shared/functions/natural-sort';
import { BcmInvoiceWdTemplate } from '@shared/models/bcm-invoice-wd-template';
import { FinancialRecordStatus } from '@shared/models/bcm-financial-record';
import { InvoicePayment, InvoicePaymentDto } from '@shared/models/invoice-payment';
import { getAddressString } from '@shared/functions/get-address-string';
import { v4 as uuidv4 } from 'uuid';
import { getPriceDataFromInvoicePositions } from '@shared/functions/getPriceDataFromInvoicePositions';

export interface InvoiceAddressStructured {
    salutation?: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    street?: string;
    zip?: string;
    city?: string;
    country?: string;
}

export interface InvoiceSentInformation {
    id: number;
    bcm_invoices_id: number;
    sentTo: string;
    information: any;
    insertedOn: Date;
    insertedBy: string;
}

export interface IInvoice {
    id?: number;
    isSepa?: boolean;
    person?: IPerson;
    company?: ICompany;
    positions: InvoicePositionDto[];
    totalDiscount: number;
    totalGrossPrice: number;
    totalNetPrice: number;
    totalTaxes: { [key: number]: number };
    totalTax: number;
    note: string;
    internalNote: string;
    date: string;
    dueDate: string;
    invoiceNumber: string;
    clerk: string;
    tenant: Tenant;
    payedOn: string;
    invoiceType: InvoiceTypes;
    cancellationInvoice: IInvoice;
    cancellationReason?: string;
    address: string;
    addressSalutation?: string;
    addressTitle?: string;
    addressFirstName?: string;
    addressLastName?: string;
    addressStreet?: string;
    addressZip?: string;
    addressCity?: string;
    addressCountry?: string;
    sentInformation: InvoiceSentInformation;
    paymentType: BcmPaymentType;
    pdfDataUrl: string;
    invoiceStatus: FinancialRecordStatus;
    searchHelpers: { [key: string]: string };
    wordTemplate?: BcmInvoiceWdTemplate;
    eInvoice?: boolean;
    subject?: string;
    taxNumber?: string;
    yourSign?: string;
    payments?: InvoicePaymentDto[];
    paidAmount?: number;
    missingAmount?: number;
}

export class Invoice {
    private readonly _id?: number;
    private _isSepa: boolean;
    private _totalDiscount = 0;
    private _totalNetPrice = 0;
    private _totalTaxFreeProductsPrice = 0;
    private _totalGrossPrice = 0;
    private _totalTaxes: { [key: number]: number };
    private _totalNetByTaxRate: { [key: number]: number } = {};
    private _totalTax = 0;
    private _person: Person;
    private _company: Company;
    private _recipientName: string;
    private _positions: InvoicePosition[] = [];
    private _positionsDirty: boolean;
    private _note: string;
    private _internalNote: string;
    private _date: Date;
    private _dueDate: Date;
    private readonly _invoiceNumber: string;
    private _clerk: string;
    private _paymentType: BcmPaymentType = BcmPaymentType.Invoice;
    private _tenant: Tenant;
    private readonly _pdfDataUrlUnsafe: string;
    private _pdfDataUrl: SafeUrl;
    private _payedOn: Date;
    private _invoiceType = InvoiceTypes.Invoice;
    private _cancellationInvoice: Invoice;
    private _address: string;
    private readonly _sentInformation: InvoiceSentInformation;
    private _wordTemplate?: BcmInvoiceWdTemplate;
    public eInvoice?: boolean;
    public invoiceStatus: FinancialRecordStatus;
    public searchHelpers: { [key: string]: string };
    public subject?: string;
    public taxNumber?: string;
    public yourSign?: string;
    public payments: InvoicePayment[] = [];
    public paidAmount?: number;
    public missingAmount?: number;
    public cancellationReason?: string;

    bcm_sepa_runs_id?: number;

    uuid: string;

    addressSalutation: string;
    addressTitle: string;
    addressFirstName: string;
    addressLastName: string;
    addressStreet: string;
    addressZip: string;
    addressCity: string;
    addressCountry: string;

    public totalGrossPriceChange = new EventEmitter<number>();

    public get id(): number {
        return this._id;
    }

    public get isSepa(): boolean {
        return this._isSepa;
    }

    public get totalDiscount(): number {
        return this._totalDiscount;
    }

    public get totalNetPrice(): number {
        return this._totalNetPrice;
    }

    public get totalTaxFreeProductsPrice(): number {
        return this._totalTaxFreeProductsPrice;
    }

    public get totalGrossPrice(): number {
        return this._totalGrossPrice;
    }

    public get totalTaxes(): { [key: number]: number } {
        return this._totalTaxes;
    }

    public get totalTaxesList(): Array<{ taxRate: number, value: number }> {
        return naturalSort(Object.keys(this._totalTaxes))
            .map((key) => ({taxRate: parseFloat(key), value: this._totalTaxes[key]}));
    }

    public get totalTaxesListFiltered(): Array<{ taxRate: number; value: number; netBase: number }> {
        return naturalSort(Object.keys(this._totalTaxes))
            .filter(key => key !== '0')
            .map((key) => ({
                taxRate: parseFloat(key),
                value: this._totalTaxes[key],
                netBase: this._totalNetByTaxRate[key] ?? 0,
            }));
    }

    public get totalTax(): number {
        return this._totalTax;
    }

    public get person(): Person {
        return this._person;
    }

    public set person(person: Person) {
        this._person = person;
    }

    public get tenant(): Tenant {
        return this._tenant;
    }

    public set tenant(tenant: Tenant) {
        this._tenant = tenant;
    }

    public get company(): Company {
        return this._company;
    }

    public set company(company: Company) {
        this._company = company;
    }

    public get recipientName(): string {
        return this._recipientName;
    }

    public set recipientName(recipientName: string) {
        this._recipientName = recipientName;
    }

    public get positions(): InvoicePosition[] {
        return this._positions;
    }

    public get positionsDirty(): boolean {
        return this._positionsDirty;
    }

    public get note(): string {
        return this._note;
    }

    public set note(note: string) {
        this._note = note;
    }

    public get internalNote(): string {
        return this._internalNote;
    }

    public set internalNote(note: string) {
        this._internalNote = note;
    }

    public get date(): Date {
        return this._date;
    }

    public set date(date: Date) {
        this._date = date;
    }

    public get dueDate(): Date {
        return this._dueDate;
    }

    public set dueDate(dueDate: Date) {
        this._dueDate = dueDate;
    }

    public get invoiceNumber(): string {
        return this._invoiceNumber;
    }

    public get clerk(): string {
        return this._clerk;
    }

    public set clerk(clerk: string) {
        this._clerk = clerk;
    }

    public get paymentType(): BcmPaymentType {
        return this._paymentType;
    }

    public set paymentType(paymentType: BcmPaymentType) {
        this._paymentType = paymentType;
    }

    public get pdfDataUrl(): SafeUrl {
        return this._pdfDataUrl;
    }

    public set pdfDataUrl(pdfDataUrl: SafeUrl) {
        this._pdfDataUrl = pdfDataUrl;
    }

    public get pdfDataUrlUnsafe(): string {
        return this._pdfDataUrlUnsafe;
    }

    public set payedOn(payedOn: Date) {
        this._payedOn = payedOn;
    }

    public get payedOn(): Date {
        return this._payedOn;
    }

    public set invoiceType(invoiceType: InvoiceTypes) {
        this._invoiceType = invoiceType;
    }

    public get invoiceType(): InvoiceTypes {
        return this._invoiceType;
    }

    public set wordTemplate(wordTemplate: BcmInvoiceWdTemplate) {
        this._wordTemplate = wordTemplate;
    }

    public get wordTemplate(): BcmInvoiceWdTemplate {
        return this._wordTemplate;
    }

    public set cancellationInvoice(cancellationInvoice: Invoice) {
        this._cancellationInvoice = cancellationInvoice;
    }

    public get cancellationInvoice(): Invoice {
        return this._cancellationInvoice;
    }

    public set address(address: string) {
        if (this._person?.id != null) {
            this._isSepa = this._person.SEPAMandat;
        }

        if (this._company?.id != null) {
            this._isSepa = this._company.SEPAMandat;
        }

        this._address = address;
    }

    public get address(): string {
        return this._address;
    }

    public get hasStructuredAddress(): boolean {
        return !!(
            this.addressFirstName ||
            this.addressLastName ||
            this.addressStreet ||
            this.addressZip ||
            this.addressCity
        );
    }

    public get legacyAddress(): string | null {
        return this.hasStructuredAddress ? null : (this._address || null);
    }

    public get addressView(): string {
        if (this._person?.id != null) {
            return getAddressString(this._person, '<br>');
        }

        if (this._company?.id != null) {
            return getAddressString(this._company, '<br>');
        }

        return this.address?.split('\n').join('<br>');
    }

    public get sentInformation(): InvoiceSentInformation {
        return this._sentInformation;
    }

    constructor(invoiceRaw: IInvoice = {} as IInvoice) {
        this._id = invoiceRaw.id;
        this._person = new Person(invoiceRaw.person || undefined);
        this._company = new Company(invoiceRaw.company || undefined);
        this._recipientName = invoiceRaw.address;
        this._address = invoiceRaw.address;
        this.addressSalutation = invoiceRaw.addressSalutation ?? '';
        this.addressTitle = invoiceRaw.addressTitle ?? '';
        this.addressFirstName = invoiceRaw.addressFirstName ?? '';
        this.addressLastName = invoiceRaw.addressLastName ?? '';
        this.addressStreet = invoiceRaw.addressStreet ?? '';
        this.addressZip = invoiceRaw.addressZip ?? '';
        this.addressCity = invoiceRaw.addressCity ?? '';
        this.addressCountry = invoiceRaw.addressCountry ?? '';
        this._tenant = new Tenant(undefined);
        this._positions = (invoiceRaw.positions || []).map(pos => new InvoicePosition(pos));
        this._note = invoiceRaw.note;
        this._internalNote = invoiceRaw.internalNote;
        this._sentInformation = invoiceRaw.sentInformation;
        this.subject = invoiceRaw.subject;
        this.taxNumber = invoiceRaw.taxNumber;
        this.yourSign = invoiceRaw.yourSign;

        const date = parseISO(invoiceRaw.date);
        const dueDate = parseISO(invoiceRaw.dueDate);
        const payedOn = parseISO(invoiceRaw.payedOn);

        this._date = isValid(date) ? date : new Date();

        if (isValid(dueDate)) {
            this._dueDate = dueDate;
        }

        this._invoiceNumber = invoiceRaw.invoiceNumber;
        this._clerk = invoiceRaw.clerk;
        this._paymentType = invoiceRaw.paymentType || BcmPaymentType.Invoice;
        this._pdfDataUrlUnsafe = invoiceRaw.pdfDataUrl;
        this._invoiceType = invoiceRaw.invoiceType || InvoiceTypes.Invoice;
        this._payedOn = isValid(payedOn) ? payedOn : null;
        this._cancellationInvoice = invoiceRaw.cancellationInvoice ? new Invoice(invoiceRaw.cancellationInvoice) : null;
        this.cancellationReason = invoiceRaw.cancellationReason || null;
        this.invoiceStatus = invoiceRaw.invoiceStatus || FinancialRecordStatus.Open;
        this.updatePrices();

        if (invoiceRaw.totalGrossPrice != null && this._positions.length === 0) {
            this._totalGrossPrice = invoiceRaw.totalGrossPrice;
        }

        this._wordTemplate = invoiceRaw.wordTemplate ? new BcmInvoiceWdTemplate(invoiceRaw.wordTemplate) : null;
        this.eInvoice = invoiceRaw.eInvoice === true;

        this.payments = (invoiceRaw.payments || []).map(payment => new InvoicePayment(payment));

        if (invoiceRaw.paidAmount != null && invoiceRaw.missingAmount != null) {
            this.paidAmount = invoiceRaw.paidAmount;
            this.missingAmount = invoiceRaw.missingAmount;
        } else {
            this.updatePaymentValues();
        }

        this.searchHelpers = {
            ...invoiceRaw.searchHelpers,
            name: this._person?.id
                ? this._person.toString(true)
                : this._company.toString(),
            invoiceNumber: this.invoiceNumber,
            cancelInvoiceNumber: this._cancellationInvoice?.invoiceNumber,
            price: this._totalGrossPrice.toString(),
        };

        this.uuid = uuidv4();
    }

    public removePositionsFromPreviousInvoiceRecipient(): void {
        this._positions = this._positions.filter(p => p.id == null);
        this._positionsDirty = true;
        this.updatePrices();
    }

    public addPosition(newPosition: InvoicePosition): void {
        this._positions.push(newPosition);
        this._positionsDirty = true;
        this.updatePrices();
    }

    public removePosition(positionToRemove: InvoicePosition, index: number): void {
        this._positions.splice(index, 1);
        this._positionsDirty = true;
        this.updatePrices();
    }

    public updatePrices(): void {
        const signMultiplier: 1 | -1 = [
            InvoiceTypes.Invoice,
            InvoiceTypes.Crediting,
            ReceiptTypes.Receipt
        ].includes(this.invoiceType)
            ? 1
            : -1;

        const priceData = getPriceDataFromInvoicePositions(this._positions, signMultiplier);

        this._totalNetPrice = priceData.totalNetPrice;
        this._totalGrossPrice = priceData.totalGrossPrice;
        this._totalTaxFreeProductsPrice = priceData.totalTaxFreeProductsPrice;
        this._totalTax = priceData.totalTax;

        this.totalGrossPriceChange.emit(this._totalGrossPrice);

        this._totalTaxes = priceData.totalTaxes;
        this._totalNetByTaxRate = priceData.totalNetByTaxRate;
    }

    public updatePaymentValues() {
        this.paidAmount = this.payments.reduce(
            (accumulator, currentPayment) => accumulator + currentPayment.amount,
            0,
        );
        this.missingAmount = invoiceMissingAmount(this.totalGrossPrice, this.paidAmount);
    }
}

// Minus-Rechnung/-Gutschrift: Auszahlungen gelten unabhängig vom gespeicherten Vorzeichen als bezahlt
export function invoiceMissingAmount(totalGrossPrice: number, paidAmount: number): number {
    return totalGrossPrice < 0
        ? totalGrossPrice + Math.abs(paidAmount)
        : totalGrossPrice - paidAmount;
}
